import { useState } from 'react';
import { Formik } from 'formik';
import { TextField, Box, Button, Grid, StepLabel } from 'components/ui';
import { WideForm } from 'components/styles_components/form';
import { StyledStepper, StyledStep } from 'components/styles_components/stepper';
import { FormattedMessage, useIntl } from 'react-intl';
import { FormHintTypography } from 'components/styles_components/typography';
import PlaceAutocomplete from 'components/form/autocomplete/place';
import TemplateAutocomplete from 'components/form/autocomplete/template';
import InspectionSubjectAutocomplete from 'components/form/autocomplete/inspection_subject';
import UserAutocomplete from 'components/form/autocomplete/user';
import PeriodSelect from 'components/form/period_select';
import DatePicker from 'components/form/date_picker';
import validationSchema from 'validation/inspection/create';
import DragAndDropField from 'components/inspection_field/drag_and_drop_field';

const steps = ['stepper.base_info', 'stepper.inspection_info', 'stepper.check_list'];

const requiredFields = {
  0: ['placeId', 'name'],
  1: ['periodNumber', 'periodCycle'],
  2: [],
};

const Form = ({ initialValues, processing, onSubmit, onCancel }) => {
  const intl = useIntl();
  const [activeStep, setActiveStep] = useState(0);
  const [helpVisible, setHelpVisible] = useState(false);

  const isStepValid = (errors, values, step) => {
    return requiredFields[step].every((field) => {
      return !errors[field] && values[field];
    });
  };

  const handleSubmit = (values) => {
    values.inspectionFields = values.inspectionFields.map((field, index) => {
      field.order = index;
      return field;
    });
    onSubmit(values);
  };

  const handleNext = (e) => {
    e.preventDefault();
    if (activeStep === steps.length - 1) {
      return;
    }
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleKeyDown = (e, formik, activeStep) => {
    if (
      e.keyCode === 13 &&
      e.shiftKey === false &&
      isStepValid(formik.errors, formik.values, activeStep)
    ) {
      e.preventDefault();
      handleNext(e);
      return false;
    }
  };

  const getStepContent = (step, formik) => {
    switch (step) {
      case 0:
        return (
          <>
            <PlaceAutocomplete
              margin="dense"
              id="placeId"
              name="placeId"
              label={intl.formatMessage({ id: 'place' }) + ' *'}
              value={formik.values.placeId}
              onChange={formik.handleChange}
              setValue={formik.setFieldValue}
              onBlur={formik.handleBlur}
              error={
                (formik.touched.placeId && Boolean(formik.errors.placeId)) || undefined
              }
              helperText={formik.touched.placeId && formik.errors.placeId}
            />
            <TextField
              fullWidth
              margin="dense"
              id="name"
              name="name"
              label={intl.formatMessage({ id: 'name' }) + ' *'}
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={(formik.touched.name && Boolean(formik.errors.name)) || undefined}
              helperText={formik.touched.name && formik.errors.name}
            />
            <InspectionSubjectAutocomplete
              margin="dense"
              id="inspectionSubjectId"
              name="inspectionSubjectId"
              setValue={formik.setFieldValue}
              label={intl.formatMessage({ id: 'inspection_subject' })}
              placeId={formik.values.placeId}
              value={formik.values.inspectionSubjectId}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                (formik.touched.inspectionSubjectId &&
                  Boolean(formik.errors.inspectionSubjectId)) ||
                undefined
              }
              helperText={formik.touched.inspectionSubjectId && formik.errors.inspectionSubjectId}
            />
            <UserAutocomplete
              margin="dense"
              id="inspectorId"
              name="inspectorId"
              placeId={formik.values.placeId}
              setValue={formik.setFieldValue}
              label={intl.formatMessage({ id: 'inspector' })}
              value={formik.values.inspectorId}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                (formik.touched.inspectorId && Boolean(formik.errors.inspectorId)) || undefined
              }
              helperText={formik.touched.inspectorId && formik.errors.inspectorId}
            />
          </>
        );
      case 1:
        return (
          <>
            <TemplateAutocomplete
              margin="dense"
              id="templateId"
              name="templateId"
              label={intl.formatMessage({ id: 'inspection.template' })}
              value={formik.values.templateId}
              values={formik.values}
              onChange={formik.handleChange}
              setValue={formik.setFieldValue}
              onBlur={formik.handleBlur}
            />
            {helpVisible && (
              <FormHintTypography>
                <FormattedMessage id="form.inspection.create.template.hint" />
              </FormHintTypography>
            )}
            <Grid container columnSpacing={2} className="margin-top-10">
              <Grid size={{ xs: 6, md: 3 }}>
                <TextField
                  fullWidth
                  margin="dense"
                  type="number"
                  id="periodNumber"
                  name="periodNumber"
                  inputProps={{ min: 1 }}
                  label={intl.formatMessage({ id: 'inspection.period_number' }) + ' *'}
                  value={formik.values.periodNumber}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    (formik.touched.periodNumber && Boolean(formik.errors.periodNumber)) ||
                    undefined
                  }
                  helperText={formik.touched.periodNumber && formik.errors.periodNumber}
                />
              </Grid>
              <Grid size={{ xs: 6, md: 9 }}>
                <PeriodSelect
                  fullWidth
                  margin="dense"
                  id="periodCycle"
                  name="periodCycle"
                  label={intl.formatMessage({ id: 'inspection.period_cycle' }) + ' *'}
                  value={formik.values.periodCycle}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    (formik.touched.periodCycle && Boolean(formik.errors.periodCycle)) || undefined
                  }
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                {helpVisible && (
                  <FormHintTypography>
                    <FormattedMessage id="form.inspection.create.period.hint" />
                  </FormHintTypography>
                )}
              </Grid>
            </Grid>
            <Grid>
              <DatePicker
                className="full-width margin-top-10"
                name="doneAt"
                id="doneAt"
                label={intl.formatMessage({ id: 'form.inspection.create.done_at' })}
                value={formik.values.doneAt}
                onChange={(value) => formik.setFieldValue('doneAt', value)}
              />
              {helpVisible && (
                <FormHintTypography>
                  <FormattedMessage id="form.inspection.create.done_at.hint" />
                </FormHintTypography>
              )}
            </Grid>
            <Grid className="margin-top-10">
              <DatePicker
                className="full-width"
                name="dueDate"
                id="dueDate"
                label={intl.formatMessage({ id: 'form.inspection.create.due_date' })}
                value={formik.values.dueDate}
                onChange={(value) => formik.setFieldValue('dueDate', value)}
              />
              {helpVisible && (
                <FormHintTypography>
                  <FormattedMessage id="form.inspection.create.due_date.hint" />
                </FormHintTypography>
              )}
            </Grid>
            <Box display="flex" justifyContent="flex-end">
              <Button
                variant="text"
                size="small"
                color="info"
                disabled={processing}
                onClick={() => setHelpVisible(!helpVisible)}
              >
                <FormattedMessage id={helpVisible ? 'button.help.hide' : 'button.help.show'} />
              </Button>
            </Box>
          </>
        );
      case 2:
        return (
          <>
            <DragAndDropField
              margin="dense"
              id="inspectionFields"
              name="inspectionFields"
              formik={formik}
              processing={processing}
            />
            <TextField
              fullWidth
              margin="dense"
              multiline
              rows={4}
              id="notes"
              name="notes"
              label={intl.formatMessage({ id: 'inspection.notes' })}
              value={formik.values.notes}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={(formik.touched.notes && Boolean(formik.errors.notes)) || undefined}
              helperText={formik.touched.notes && formik.errors.notes}
            />
          </>
        );
      default:
        return 'Unknown step';
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema(intl)}
      onSubmit={handleSubmit}
    >
      {(formik) => {
        return (
          <WideForm
            onSubmit={formik.handleSubmit}
            onKeyDown={(e) => handleKeyDown(e, formik, activeStep)}
          >
            <StyledStepper activeStep={activeStep}>
              {steps.map((id) => (
                <StyledStep key={id}>
                  <StepLabel>
                    <FormattedMessage id={id} />
                  </StepLabel>
                </StyledStep>
              ))}
            </StyledStepper>
            <Box className="margin-top-10">{getStepContent(activeStep, formik)}</Box>
            <Box display="flex" justifyContent="space-between" className="margin-top-10">
              <Button variant="text" size="small" disabled={processing} onClick={onCancel}>
                <FormattedMessage id="button.cancel" />
              </Button>
              <Box display="flex" justifyContent="flex-end">
                {activeStep !== 0 && (
                  <Button onClick={handleBack} sx={{ mr: 1 }} variant="outlined" color="inherit">
                    <FormattedMessage id="button.back" />
                  </Button>
                )}
                {activeStep === steps.length - 1 ? (
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    disabled={processing || !formik.isValid}
                  >
                    <FormattedMessage id="button.save" />
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleNext}
                    disabled={!isStepValid(formik.errors, formik.values, activeStep)}
                  >
                    <FormattedMessage id="button.next" />
                  </Button>
                )}
              </Box>
            </Box>
          </WideForm>
        );
      }}
    </Formik>
  );
};

export default Form;
